import React, { useState, useEffect, useRef } from 'react';

const COMMON_ICONS = [
  'ri-star-line', 'ri-star-fill', 'ri-heart-line', 'ri-heart-fill',
  'ri-briefcase-line', 'ri-briefcase-fill', 'ri-checkbox-circle-line', 'ri-checkbox-circle-fill',
  'ri-lightbulb-line', 'ri-lightbulb-fill', 'ri-rocket-line', 'ri-rocket-fill',
  'ri-shield-check-line', 'ri-shield-check-fill', 'ri-bar-chart-line', 'ri-bar-chart-fill',
  'ri-line-chart-line', 'ri-pie-chart-line', 'ri-medal-line', 'ri-medal-fill',
  'ri-customer-service-2-line', 'ri-customer-service-2-fill', 'ri-group-line', 'ri-group-fill',
  'ri-computer-line', 'ri-smartphone-line', 'ri-cloud-line', 'ri-database-2-line',
  'ri-server-line', 'ri-settings-3-line', 'ri-tools-line', 'ri-brush-line',
  'ri-pantone-line', 'ri-pen-nib-line', 'ri-layout-masonry-line', 'ri-code-s-slash-line',
  'ri-bug-line', 'ri-terminal-box-line', 'ri-global-line', 'ri-earth-line',
  'ri-shopping-cart-2-line', 'ri-store-2-line', 'ri-bank-card-line', 'ri-wallet-3-line',
  'ri-exchange-dollar-line', 'ri-safe-line', 'ri-truck-line', 'ri-ship-line',
  'ri-flight-takeoff-line', 'ri-map-pin-line', 'ri-home-4-line', 'ri-building-2-line',
  'ri-hospital-line', 'ri-government-line', 'ri-book-read-line', 'ri-graduation-cap-line',
  'ri-stethoscope-line', 'ri-capsule-line', 'ri-leaf-line', 'ri-seedling-line',
  'ri-fire-line', 'ri-drop-line', 'ri-thunderstorms-line', 'ri-sun-line',
  'ri-moon-line', 'ri-time-line', 'ri-calendar-event-line', 'ri-camera-line',
  'ri-image-line', 'ri-video-line', 'ri-headphone-line', 'ri-mic-line',
  'ri-file-text-line', 'ri-folder-open-line', 'ri-mail-send-line', 'ri-message-3-line',
  'ri-chat-3-line', 'ri-megaphone-line', 'ri-notification-3-line', 'ri-alarm-warning-line',
  'ri-search-line', 'ri-zoom-in-line', 'ri-link', 'ri-share-line',
  'ri-download-cloud-line', 'ri-upload-cloud-line', 'ri-refresh-line', 'ri-check-line',
  'ri-close-line', 'ri-add-line', 'ri-subtract-line', 'ri-arrow-right-line'
];

interface IconPickerProps {
  value: string;
  onChange: (icon: string) => void;
  className?: string;
}

export default function IconPicker({ value, onChange, className = '' }: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const filteredIcons = COMMON_ICONS.filter(icon => 
    icon.toLowerCase().includes(search.toLowerCase().replace(/\s+/g, '-'))
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm hover:border-gray-400 focus:outline-none transition-colors"
      >
        <div className="flex items-center gap-2">
          <i className={`${value} text-gray-700 text-lg`}></i>
          <span className="text-gray-600 truncate">{value}</span>
        </div>
        <i className="ri-arrow-down-s-line text-gray-400"></i>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg flex flex-col max-h-64 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/30 transition-shadow"
                placeholder="Search icons..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="p-2 overflow-y-auto grid grid-cols-6 gap-1">
            {filteredIcons.length > 0 ? (
              filteredIcons.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => {
                    onChange(icon);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
                    value === icon ? 'bg-gold/10 text-gold' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  title={icon}
                >
                  <i className={`${icon} text-xl`}></i>
                </button>
              ))
            ) : (
              <div className="col-span-6 py-4 text-center text-xs text-gray-500">
                No icons found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
